import { useId } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import colors from "../../config/colors";
import { colorPalette } from "../../config/colorPalette";
import { getStatus, labelSx, messageSx, textSx } from "../_shared/fieldStyles";

const { brand, mercury, mercuryAlpha, red } = colorPalette;

const ISO = "YYYY-MM-DD";

/**
 * DateInput
 *
 * A date field that opens a calendar under the field's calendar icon (the browser's own date
 * picker opens at the left edge of a wide field, away from the icon).
 *
 * Props:
 * - label: text above the field
 * - value: "YYYY-MM-DD" or ""
 * - onChange: (value) => void, called with "YYYY-MM-DD" ("" when cleared or incomplete)
 * - min: "YYYY-MM-DD", earlier days can't be picked
 * - placeholder: shown in the empty field (default "DD/MM/YYYY")
 * - error: true, or a message string shown under the field (red state)
 * - readOnly, disabled
 * - id: input id (generated when omitted)
 * - sx: extra styles for the whole field, merged last
 */
const DateInput = ({
  label,
  value,
  onChange,
  min,
  placeholder = "DD/MM/YYYY",
  error,
  readOnly = false,
  disabled = false,
  id,
  sx: sxOverride = {},
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const status = getStatus({ error, disabled });
  const message = typeof error === "string" ? error : null;
  const parsed = value ? dayjs(value, ISO) : null;
  // Same colours as the other inputs' disabled / error / default states (fieldStyles).
  const border =
    status === "disabled"
      ? mercury[300]
      : status === "error"
        ? red[300]
        : mercuryAlpha[8];
  const background =
    status === "disabled"
      ? mercury[100]
      : status === "error"
        ? red[100]
        : colors.white;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        ...sxOverride,
      }}
    >
      {label && (
        <Box component="label" htmlFor={inputId} sx={labelSx(disabled)}>
          {label}
        </Box>
      )}
      <DatePicker
        value={parsed && parsed.isValid() ? parsed : null}
        onChange={(next) =>
          onChange?.(next && next.isValid() ? next.format(ISO) : "")
        }
        minDate={min ? dayjs(min, ISO) : undefined}
        format="DD/MM/YYYY"
        // Separate day / month / year parts, so the ones not typed yet can be styled as a placeholder.
        enableAccessibleFieldDOMStructure
        readOnly={readOnly}
        disabled={disabled}
        // Open under the calendar icon, at the field's right edge.
        slotProps={{
          popper: { placement: "bottom-end" },
          textField: {
            id: inputId,
            placeholder,
            error: status === "error",
            fullWidth: true,
            sx: {
              "& .MuiPickersOutlinedInput-root, & .MuiOutlinedInput-root": {
                ...textSx,
                height: 44,
                borderRadius: "8px",
                paddingRight: "11px",
                backgroundColor: background,
                color: disabled ? mercuryAlpha[28] : mercury[950],
              },
              "& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline":
                { border: `1px solid ${border}` },
              "& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                { borderColor: border },
              // The picker's own focus style (a 2px blue border) has the same specificity, so this
              // one is !important to keep the 1px brand border the other inputs have.
              "& .Mui-focused .MuiPickersOutlinedInput-notchedOutline, & .Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: `1px solid ${status === "error" ? border : brand[700]} !important`,
                },
              // The picker's own disabled border (darker grey) outranks the rule above.
              "& .Mui-disabled .MuiPickersOutlinedInput-notchedOutline, & .Mui-disabled .MuiOutlinedInput-notchedOutline":
                { border: `1px solid ${border} !important` },
              "& .MuiIconButton-root": {
                color: disabled ? mercuryAlpha[28] : mercuryAlpha[65],
              },
              // Parts not typed yet (DD, MM, YYYY) read as a placeholder, like the other
              // inputs; typed parts keep the text colour.
              "& .MuiPickersSectionList-sectionContent[aria-valuetext='Empty'], & .MuiPickersInputBase-sectionBefore, & .MuiPickersInputBase-sectionAfter":
                { color: mercuryAlpha[65] },
            },
          },
        }}
      />
      {message && (
        <Box id={`${inputId}-error`} role="alert" sx={messageSx}>
          {message}
        </Box>
      )}
    </Box>
  );
};

export default DateInput;
